import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import fontRegularUrl from "../fonts/HelveticaNeue-Regular/HelveticaNeue.ttf";
import fontBoldUrl from "../fonts/HelveticaNeue-Bold/HelveticaNeue-Bold.ttf";
import grownuLogoUrl from "../assets/Grownu logo on white.png";
import numeral from 'numeral'
import moment from 'moment'
import lt from '../translations/lt.json'
import es from '../translations/es.json'
import sv from '../translations/sv.json'

function formatCurrency(n, currency = "EUR") {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(n)
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

function hexToRgb(hex) {
  const m = hex.replace("#", "");
  const n = parseInt(m.length === 3 ? m.split("").map((c) => c + c).join("") : m, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function chunkArray(arr, size = 3) {
  return Array.from(
    { length: Math.ceil(arr.length / size) },
    (_, i) => arr.slice(i * size, i * size + size)
  );
}

const PDF_FONT_FAMILY = "HelveticaNeue";
const PDF_FONT_REGULAR_FILE = "HelveticaNeue-Regular.ttf";
const PDF_FONT_BOLD_FILE = "HelveticaNeue-Bold.ttf";
let fontDataPromise;
let poweredByLogoDataUrlPromise;

function arrayBufferToBase64(arrayBuffer) {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

async function fetchFontAsBase64(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load font: ${url}`);
  }
  const buffer = await response.arrayBuffer();
  return arrayBufferToBase64(buffer);
}

async function fetchImageAsDataUrl(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to load image: ${url}`);
  }
  const blob = await response.blob();
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Failed to read image: ${url}`));
    reader.readAsDataURL(blob);
  });
}

async function getPoweredByLogoDataUrl() {
  if (!poweredByLogoDataUrlPromise) {
    poweredByLogoDataUrlPromise = fetchImageAsDataUrl(grownuLogoUrl);
  }
  return poweredByLogoDataUrlPromise;
}

async function ensurePdfFontsRegistered(doc) {
  if (!fontDataPromise) {
    fontDataPromise = (async () => {
      const [regularBase64, boldBase64] = await Promise.all([
        fetchFontAsBase64(fontRegularUrl),
        fetchFontAsBase64(fontBoldUrl),
      ]);
      return { regularBase64, boldBase64 };
    })();
  }

  const { regularBase64, boldBase64 } = await fontDataPromise;
  doc.addFileToVFS(PDF_FONT_REGULAR_FILE, regularBase64);
  doc.addFileToVFS(PDF_FONT_BOLD_FILE, boldBase64);
  doc.addFont(PDF_FONT_REGULAR_FILE, PDF_FONT_FAMILY, "normal");
  doc.addFont(PDF_FONT_BOLD_FILE, PDF_FONT_FAMILY, "bold");
}

const initT = (lang) => {
  return (string) => {
    if (lang === 'lt') return lt[string] || string
    if (lang === 'es') return es[string] || string
    if (lang === 'sv') return sv[string] || string
    return string
  }
}

export async function generateInvoicePdf({ invoice, customer, paymentInfo, projects, template, header }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  await ensurePdfFontsRegistered(doc);
  const accent = hexToRgb(template?.accent ?? "#3b3b80");
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 48;

  const t = initT(invoice.language)

  // Top accent bar
  doc.setFillColor(accent[0], accent[1], accent[2]);
  doc.rect(0, 0, pageW, 6, "F");

  // Issuer
  doc.setFont(PDF_FONT_FAMILY, "bold");
  doc.setFontSize(18);
  doc.setTextColor(accent[0], accent[1], accent[2]);
  doc.text(header.companyName, margin, 56);
  doc.setFont(PDF_FONT_FAMILY, "normal");
  doc.setFontSize(9);
  doc.setTextColor(110);
  doc.text(`${header.companyCity} · ${header.companyAddress}`, margin, 70);
  doc.text(`${t('VAT')} ${header.companyVatNumber} · ${header.companyBillingEmail}`, margin, 82);

  // Invoice title
  doc.setFont(PDF_FONT_FAMILY, "bold");
  doc.setFontSize(22);
  doc.setTextColor(accent[0], accent[1], accent[2]);
  doc.text(t("INVOICE"), pageW - margin, 56, { align: "right" });
  doc.setFont(PDF_FONT_FAMILY, "normal");
  doc.setFontSize(10);
  doc.setTextColor(40);
  doc.text(invoice.invoiceNumber, pageW - margin, 72, { align: "right" });

  // Bill-to / Project / Dates row
  const rowY = 110;
  doc.setDrawColor(220);
  doc.line(margin, rowY - 10, pageW - margin, rowY - 10);
  doc.setFontSize(8);
  doc.setTextColor(140);
  doc.text(t("BILL TO"), margin, rowY);
  doc.text(t("PROJECT"), margin + 180, rowY);
  doc.text(t("DATES"), pageW - margin, rowY, { align: "right" });

  doc.setFontSize(10);
  doc.setTextColor(20);
  doc.setFont(PDF_FONT_FAMILY, "bold");
  doc.text(customer?.name ?? "—", margin, rowY + 14);
  doc.setFont(PDF_FONT_FAMILY, "normal");
  doc.setFontSize(9);
  doc.setTextColor(90);
  const addressLines = [customer.address1, customer.address2, customer.address3].filter(Boolean);
  addressLines.forEach((l, i) => doc.text(l, margin, rowY + 28 + i * 11));
  if (customer?.vatNumber) {
    doc.text(`${t('VAT')} ${customer.vatNumber}`, margin, rowY + 28 + addressLines.length * 11)
  };

  if (customer?.registrationNumber) {
    doc.text(`Reg. ${customer.registrationNumber}`, margin, rowY + 28 + addressLines.length * 11 + 11)
  }

  doc.setFont(PDF_FONT_FAMILY, "bold");
  doc.setFontSize(10);
  doc.setTextColor(20);
  let projectOffset = 0
  projects.forEach((projectName, i) => {
    doc.text(projectName, margin + 180, rowY + 14 + i * 11);
    projectOffset += 11;
  })
  // doc.text(project?.name ?? "—", margin + 180, rowY + 14);
  // doc.setFont(PDF_FONT_FAMILY, "normal");
  // doc.setFontSize(9);
  // doc.setTextColor(90);
  // if (project?.code) doc.text(project.code, margin + 180, rowY + 28);
  doc.text(`${t('Period')}: ${invoice.periodStart} – ${invoice.periodEnd}`, margin + 180, rowY + 20 + projectOffset);

  doc.setFontSize(9);
  doc.setTextColor(40);
  doc.text(`${t('Issue')}: ${formatDate(invoice.invoiceDate)}`, pageW - margin, rowY + 14, { align: "right" });
  doc.text(`${t('Due')}: ${formatDate(invoice.dueDate)}`, pageW - margin, rowY + 28, { align: "right" });
  doc.text(`${t('Terms')}: ${invoice.paymentTerms}`, pageW - margin, rowY + 42, { align: "right" });

  // Line items table
  const lineRows = invoice.lines.length
    ? invoice.lines.map((l) => [
        l.description,
        `${l.quantity}${l.unit ? " " + l.unit : ""}`,
        formatCurrency(l.unitPrice, invoice.currency),
        formatCurrency(l.taxRate, invoice.currency),
        formatCurrency(l.total, invoice.currency),
      ])
    : [["No line items", "", "", "", ""]];

  autoTable(doc, {
    startY: rowY + 80,
    head: [[t('Description'), t('Qty'), t('Rate'), t('VAT'), t('Amount')]],
    body: lineRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 9, font: PDF_FONT_FAMILY, cellPadding: 6, lineHeight: 0.9, },
    headStyles: { fillColor: accent, textColor: 255, fontStyle: "bold" },
    columnStyles: {
      1: { halign: "right", cellWidth: 50 },
      2: { halign: "right", cellWidth: 70 },
      3: { halign: "right", cellWidth: 50 },
      4: { halign: "right", cellWidth: 80 },
    },
  });

  // Totals
  // @ts-expect-error jspdf-autotable adds lastAutoTable
  const afterTableY = (doc.lastAutoTable?.finalY ?? rowY + 200) + 16;
  const totalsX = pageW - margin - 200;
  let ty = afterTableY;
  doc.setFontSize(10);
  const drawRow = (label, value, bold = false) => {
    doc.setFont(PDF_FONT_FAMILY, bold ? "bold" : "normal");
    doc.setTextColor(bold ? 20 : 90);
    doc.text(label, totalsX, ty);
    doc.text(value, pageW - margin, ty, { align: "right" });
    ty += bold ? 18 : 14;
  };
  drawRow(t('Labor'), formatCurrency(invoice.subtotalLabor, invoice.currency));
  drawRow(t('Expenses'), formatCurrency(invoice.subtotalExpenses, invoice.currency));
  if (invoice.subtotalManual > 0) drawRow(t('Manual lines'), formatCurrency(invoice.subtotalManual, invoice.currency));
  doc.setDrawColor(200);
  doc.line(totalsX, ty - 6, pageW - margin, ty - 6);
  ty += 6;
  if (invoice.discountTotal > 0) drawRow(t('Discount'), `- ${formatCurrency(invoice.discountTotal, invoice.currency)}`);
  drawRow(t('Net Total'), formatCurrency(invoice.totalNet, invoice.currency));
  doc.setDrawColor(200);
  doc.line(totalsX, ty - 6, pageW - margin, ty - 6);
  ty += 6;
  drawRow(t('VAT / Tax'), formatCurrency(invoice.taxTotal, invoice.currency));
  doc.setDrawColor(200);
  doc.line(totalsX, ty - 6, pageW - margin, ty - 6);
  ty += 6;
  doc.setTextColor(accent[0], accent[1], accent[2]);
  drawRow(t('TOTAL DUE'), formatCurrency(invoice.grandTotal, invoice.currency), true);

  // Notes
  if (invoice.notes) {
    doc.setFont(PDF_FONT_FAMILY, "bold");
    doc.setFontSize(8);
    doc.setTextColor(140);
    doc.text(t('NOTES'), margin, ty + 10);
    doc.setFont(PDF_FONT_FAMILY, "normal");
    doc.setFontSize(9);
    doc.setTextColor(70);
    const split = doc.splitTextToSize(invoice.notes, pageW - margin * 2);
    doc.text(split, margin, ty + 24);
  }

  // Footer
  const pageH = doc.internal.pageSize.getHeight();
  const paymentLines = Object.values(paymentInfo ?? {}).filter((line) => String(line ?? "").trim().length > 0);
  const safePaymentLines = paymentLines.length ? paymentLines : ["—"];
  const paymentLineHeight = 10;
  const paymentBlockBottomY = pageH - 14;
  const paymentFirstLineY = paymentBlockBottomY - (safePaymentLines.length - 1) * paymentLineHeight;
  const paymentLabelY = paymentFirstLineY - 14;
  const footerDividerY = Math.min(pageH - 60, paymentLabelY - 8);

  doc.setDrawColor(220);
  doc.line(margin, footerDividerY, pageW - margin, footerDividerY);
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(`${t('Payment details')}:`, margin, paymentLabelY);
  safePaymentLines.forEach((line, i) => {
    doc.text(line, margin, paymentFirstLineY + i * paymentLineHeight);
  })
  doc.text(header.companyName, pageW - margin, paymentLabelY, { align: "right" });
  const poweredByText = "Powered by";
  const poweredByTextWidth = doc.getTextWidth(poweredByText);
  const poweredByLogoDataUrl = await getPoweredByLogoDataUrl();
  const poweredByGap = 4;
  const poweredByLogoWidth = 42;
  const poweredByLogoHeight = 13;
  const poweredByTotalWidth = poweredByTextWidth + poweredByGap + poweredByLogoWidth;
  const poweredByX = pageW - margin - poweredByTotalWidth;
  const poweredByY = paymentFirstLineY;
  doc.setTextColor(0, 133, 255);
  doc.text(poweredByText, poweredByX, poweredByY);
  doc.addImage(
    poweredByLogoDataUrl,
    "PNG",
    poweredByX + poweredByTextWidth + poweredByGap,
    poweredByY - poweredByLogoHeight + 4,
    poweredByLogoWidth,
    poweredByLogoHeight,
  );
  doc.link(poweredByX, poweredByY - poweredByLogoHeight, poweredByTotalWidth, poweredByLogoHeight + 4, {
    url: "https://grownu.com",
  });

  return doc;
}

export async function downloadInvoicePdf(ctx) {
  const doc = await generateInvoicePdf(ctx);
  doc.save(`${ctx.invoice.invoiceNumber}.pdf`);
}

export async function previewInvoicePdfUrl(ctx) {
  const doc = await generateInvoicePdf(ctx);
  return doc.output("bloburl").toString();
}

export function exportInvoiceRegistryXlsx(
  invoices,
  customers,
  projects,
) {
  const rows = invoices.map((inv) => {
    const c = customers.find((x) => x.id === inv.customerId);
    const p = projects.find((x) => x.id === inv.projectId);
    const net = inv.subtotalLabor + inv.subtotalExpenses + inv.subtotalManual - inv.discountTotal;
    return {
      "Invoice #": inv.invoiceNumber,
      Customer: c?.name ?? "",
      Project: p?.name ?? "",
      Status: inv.status,
      "Issue date": inv.invoiceDate,
      "Due date": inv.dueDate,
      "Period start": inv.periodStart,
      "Period end": inv.periodEnd,
      Currency: inv.currency,
      Net: net,
      VAT: inv.taxTotal,
      Gross: inv.grandTotal,
      Margin: inv.marginTotal,
      "Created by": inv.createdBy,
      "Created at": inv.createdAt,
      "Issued at": inv.issuedAt ?? "",
      "Paid at": inv.paidAt ?? "",
    };
  });

  const ws = XLSX.utils.json_to_sheet(rows);
  // Auto column widths
  const colWidths = Object.keys(rows[0] ?? {}).map((k) => ({
    wch: Math.max(k.length, ...rows.map((r) => String((r)[k] ?? "").length)) + 2,
  }));
  ws["!cols"] = colWidths;

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Invoices");
  const today = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `invoice-registry-${today}.xlsx`);
}


export const buildGroupedItem = ({ items, type, description, groupBy = '' }) => {
  const quantity = items.reduce((acc, item) => acc + numeral(item.quantity || 0).value(), 0)
  const total = items.reduce((acc, item) => acc + numeral(item.total_price || 0).value(), 0)
  const avgVat = items.reduce((acc, item) => acc + (numeral(item.vat || 0).value()), 0) / items.length

  return {
    id: `group_${type}_${groupBy}_${description}`,
    description,
    type,
    quantity: numeral(quantity).format('0.[00]'),
    unit: type === 'work_time' ? 'hrs' : '',
    vat: numeral(avgVat).format('0.00'),
    unit_price: quantity > 0 ? numeral(total / quantity).format('0.00') : numeral(total).format('0.00'),
    total_price: Number(total.toFixed(2)),
  }
}

export const groupItemsBy = (items, keySelector, type, groupBy) => {
  const groupedMap = items.reduce((acc, item) => {
    const key = keySelector(item) || '-'
    if (!acc[key]) {
      acc[key] = []
    }
    acc[key].push(item)
    return acc
  }, {})

  return Object.entries(groupedMap).map(([key, groupedItems]) => buildGroupedItem({
    items: groupedItems,
    type,
    description: `${key}`,
    groupBy,
  }))
}

const buildDescription = workTime => {
  let description = `${moment(workTime.started_at).format('YYYY-MM-DD')} ${workTime.employee?.name} ${workTime.employee?.surname}`
  if (workTime.place?.name || workTime.job_type?.title) {
    description += '\n'
    let secondRow = []
    if (workTime.place?.name) {
     secondRow.push(workTime.place.name)
    }
    if (workTime.job_type?.title) {
      secondRow.push(workTime.job_type.title)
    }
    description += secondRow.join(' • ')
  }
  description += `\n${moment(workTime.started_at).format('HH:mm')} - ${moment(workTime.finished_at).format('HH:mm')}`
  return description
}

export const convertWorkTimeToInvoiceItem = (workTime, defaultVat = '0') => {
  const workDuration = moment.duration(moment(workTime.finished_at).diff(moment(workTime.started_at)))
  const breakDuration = workTime.breaks.reduce((acc, b) => {
    const duration = moment.duration(moment(b.finished_at).diff(moment(b.started_at)))
    return acc.add(duration)
  }, moment.duration(0))

  const netDuration = moment.duration(workDuration.asMilliseconds() - breakDuration.asMilliseconds())
  const quantity = numeral(netDuration.asHours()).format('0.[00]')
  const unitPrice = numeral(workTime.profitability.cost).divide(netDuration.asHours() || 1).format('0.00')

  const vatRate = Number.isNaN(defaultVat) ? 0 : defaultVat
  const netTotal = Number(workTime.profitability?.cost) || 0
  const fallbackGrossTotal = numeral(netTotal * (1 + (vatRate / 100))).format('0.00')

  return {
    id: `work_time-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    // description: `${moment(workTime.started_at).format('YYYY-MM-DD')} ${workTime.employee.name} ${workTime.employee.surname}\n${workTime.place?.name ? workTime.place.name : ''}${workTime.job_type?.title ? ` • ${workTime.job_type.title}` : ''}\n${moment(workTime.started_at).format('HH:mm')} - ${moment(workTime.finished_at).format('HH:mm')}`,
    description: buildDescription(workTime),
    type: 'work_time',
    unit: 'hrs',
    vat: numeral(fallbackGrossTotal - netTotal).format('0.00'),
    quantity: quantity,
    unit_price: unitPrice,
    total_price: fallbackGrossTotal,
    work_time: workTime,
  }
}

export const convertExpenseToInvoiceItem = (expense, defaultVat = '0') => {
  const vatRate = Number.isNaN(defaultVat) ? 0 : defaultVat
  const netTotal = Number(expense.amount) || 0
  const grossTotal = numeral(netTotal * (1 + (vatRate / 100))).format('0.00')

  return {
    id: `expense-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    description: `${expense.vendor_name}${expense.invoice_number ? ` - ${expense.invoice_number}` : ''}`,
    type: 'expense',
    unit: '',
    vat: numeral(grossTotal - netTotal).format('0.00'),
    quantity: 1,
    unit_price: numeral(expense.amount).format('0.00'),
    total_price: grossTotal,
    place_expense: expense,
  }
}

export const getGroupedItems = (items, groupBy) => {
  const { workTimes, expenses, manualEntries } = items.reduce((acc, item) => {
    return {
      ...acc,
      workTimes: item.type === 'work_time' ? [...acc.workTimes, item] : acc.workTimes,
      expenses: item.type === 'expense' ? [...acc.expenses, item] : acc.expenses,
      manualEntries: item.type === 'manual_entry' ? [...acc.manualEntries, item] : acc.manualEntries,
    }
  }, {
    workTimes: [],
    expenses: [],
    manualEntries: [],
  })
  switch (groupBy) {
    case 'summarized':
      return [
        buildGroupedItem({items: workTimes, type: 'work_time', description: 'Billable work entries'}),
        buildGroupedItem({items: expenses, type: 'expense', description: 'Billable expenses'}),
        ...manualEntries,
      ]
    case 'by_employee':
      return [
        ...groupItemsBy(workTimes, item => item.work_time.employee ? `${item.work_time.employee.name} ${item.work_time.employee.surname}` : 'No employee', `work_time`, groupBy),
        ...expenses,
        ...manualEntries,
      ]
    case 'by_project':
      return [
        ...groupItemsBy(workTimes, item => item.work_time.place ? item.work_time.place.name : 'No project', `work_time`, groupBy),
        ...groupItemsBy(expenses, item => item.place_expense.place ? item.place_expense.place.name : 'No project', 'expense', groupBy),
        ...manualEntries,
      ]
    case 'by_date':
      return [
        ...groupItemsBy(workTimes, item => moment(item.work_time.started_at).format('YYYY-MM-DD'), `work_time`, groupBy),
        ...groupItemsBy(expenses, item => moment(item.place_expense.invoice_date).format('YYYY-MM-DD'), 'expense', groupBy),
        ...manualEntries,
      ]
    case 'by_work_type':
      return [
        ...groupItemsBy(workTimes, item => item.work_time.job_type ? item.work_time.job_type.title : 'No work type', `work_time`, groupBy),
        ...expenses,
        ...manualEntries,
      ]
    default:
      const order = {
        work_time: 0,
        expense: 1,
        manual_entry: 2,
      }
      return [...items].sort((a, b) => order[a.type] - order[b.type])
  }
}

export const buildInvoiceContext = (invoice, invoiceSettings, companyInfo) => {
  const { subtotalLabor, subtotalExpenses, subtotalManual } = invoice.items.reduce((acc, item) => {
    return {
      totalAmount: acc.totalAmount + Number(item.total_price || 0),
      subtotalLabor: acc.subtotalLabor + (item.type === 'work_time' ? numeral(item.unit_price * item.quantity).value() : 0),
      subtotalExpenses: acc.subtotalExpenses + (item.type === 'expense' ? numeral(item.unit_price * item.quantity).value() : 0),
      subtotalManual: acc.subtotalManual + (item.type === 'manual_entry' ? numeral(item.unit_price * item.quantity).value() : 0),
      taxTotal: acc.taxTotal + (Number(item.vat) ? ((Number(item.total_price) || 0) - ((Number(item.unit_price) || 0) * (Number(item.quantity) || 0))) : 0),
    }
  }, {
    totalAmount: 0,
    subtotalLabor: 0,
    subtotalExpenses: 0,
    subtotalManual: 0,
    taxTotal: 0,
  })

  const totalNet = subtotalLabor + subtotalExpenses + subtotalManual - invoice.discount
  const totalVat = totalNet * invoice.default_vat / 100
  const totalAmount = numeral(Math.max(0, totalNet + totalVat)).format('0.00')
  const totalTax = numeral(totalVat).format('0.00')

  return {
    invoice: {
      invoiceNumber: invoice.invoice_number,
      status: 'draft',
      invoiceDate: invoice.invoice_date,
      dueDate: invoice.due_date,
      currency: invoice.currency,
      language: invoice.language,
      paymentTerms: invoice.payment_terms,
      notes: invoice.notes,
      totalNet: numeral(totalNet).format('0.00'),
      // grandTotal: numeral(Math.max(0, totalAmount - invoice.discount)).format('0.00'),
      grandTotal: numeral(totalAmount).format('0.00'),
      subtotalLabor: numeral(subtotalLabor).format('0.00'),
      subtotalExpenses: numeral(subtotalExpenses).format('0.00'),
      subtotalManual: numeral(subtotalManual).format('0.00'),
      discountTotal: invoice.discount,
      // taxTotal: numeral(taxTotal).format('0.00'),
      taxTotal: numeral(totalTax).format('0.00'),
      periodStart: moment(invoice.billing_period_start).format('YYYY.MM.DD'),
      periodEnd: moment(invoice.billing_period_end).format('YYYY.MM.DD'),
      lines: invoice.items.map((item, i) => ({
        id: i,
        sourceType: item.type,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        taxRate: item.vat,
        unitPrice: item.unit_price,
        total: item.total_price,
      })),
    },
    customer: {
      id: invoice.customer?.id,
      name: invoice.customer?.display_name,
      address1: invoice.customer?.billing_address_line_1,
      address2: invoice.customer?.billing_address_line_2,
      address3: [invoice.customer?.billing_postal_code, invoice.customer?.billing_city, invoice.customer?.billing_country].filter(Boolean).join(', '),
      registrationNumber: invoice.customer?.company_registration_number,
      vatNumber: invoice.customer?.vat_number,
    },
    project: {
      name: invoice.places ? invoice.places.map(p => p.name).join(', ') : '',
    },
    projects: invoice.places ? chunkArray(invoice.places.map(p => p.name), 2).map(arr => arr.join(', ')) : [],
    template: {
      id: "t1",
      name: "Classic Professional",
      description: "Traditional invoice with company letterhead",
      bestFor: "Most B2B clients",
      layoutType: "classic",
      accent: "#000"
    },
    header: {
      companyName: companyInfo?.name || '',
      companyAddress: companyInfo?.address || '',
      companyVatNumber: companyInfo?.vat_number || '',
      companyZipCode: companyInfo?.zip_code || '',
      companyCity: companyInfo?.city || '',
      companyBillingEmail: companyInfo?.billing_email || '',
      companyRegistrationNumber: companyInfo?.registration_number || '',
    },
    paymentInfo: {
      bankName: invoiceSettings?.bank_name || '',
      swift: invoiceSettings?.swift_bit || '',
      accountNumber: invoiceSettings?.account_number || '',
      ocr: invoiceSettings?.ocr || '',
    }
  }
}

export const applyMarkUps = (lines, markUpSettings) => {
  const applyMarkupToLine = (line, markupType, markupValue) => {
    const quantity = numeral(line.quantity || 0).value() || 0
    const unitPrice = numeral(line.unit_price || 0).value() || 0
    const safeMarkupValue = numeral(markupValue || 0).value() || 0

    if (safeMarkupValue === 0) {
      return {
        ...line,
        pure_unit_price: numeral(line.unit_price).format('0.00'),
      }
    }

    const priceWithVat = line.total_price / line.quantity
    const vatRate = (priceWithVat > 0 ? ((priceWithVat - unitPrice) / unitPrice) : 0) + 1
    const nextUnitPrice = markupType === 'fixed'
      ? unitPrice + safeMarkupValue
      : unitPrice * (1 + (safeMarkupValue / 100))

    const nextTotalPrice = quantity > 0 ? nextUnitPrice * quantity * vatRate : nextUnitPrice
    const nextVat = nextTotalPrice - (nextUnitPrice * quantity)
    return {
      ...line,
      vat: numeral(nextVat).format('0.00'),
      pure_unit_price: numeral(line.unit_price).format('0.00'),
      unit_price: numeral(nextUnitPrice).format('0.00'),
      total_price: numeral(nextTotalPrice).format('0.00'),
    }
  }

  return lines.map(line => {
    if (line.type === 'work_time') {
      return applyMarkupToLine(line, markUpSettings.work_times_markup_type, markUpSettings.work_times_markup_value)
    }

    if (line.type === 'expense') {
      return applyMarkupToLine(line, markUpSettings.expenses_markup_type, markUpSettings.expenses_markup_value)
    }

    return line
  })
}

export const calculateMargin = (items, discount) => {
  const {totalAmount, pureAmount} = items.reduce((acc, item) => {
    return {
      ...acc,
      pureAmount: acc.pureAmount + ((item.type === 'work_time' || item.type === 'expense') ? numeral(item.pure_unit_price * item.quantity).value() : 0),
      totalAmount: acc.totalAmount + numeral(item.unit_price * item.quantity).value()
    }
  }, {
    pureAmount: 0,
    totalAmount: 0,
  })

  const totalAmountWithDiscount = totalAmount - discount

  return numeral(Math.max(0, totalAmountWithDiscount - pureAmount)).format('0.00')
} 

export const invoiceTemplates = [
  { id: "t1", name: "Classic Professional", description: "Traditional invoice with company letterhead", bestFor: "Most B2B clients", layoutType: "classic", accent: "#3b3b80" },
  { id: "t2", name: "Modern Minimal", description: "Clean, spacious, typography-led", bestFor: "Design-forward clients", layoutType: "modern", accent: "#0f172a" },
  { id: "t3", name: "Detailed Timesheet", description: "Includes full breakdown of work entries", bestFor: "Hourly billing & consulting", layoutType: "timesheet", accent: "#1e6f4f" },
  { id: "t4", name: "Project Summary", description: "Grouped by milestones and phases", bestFor: "Fixed-fee project work", layoutType: "project", accent: "#9a3412" },
  { id: "t5", name: "Expense-Heavy", description: "Optimised for reimbursable cost recovery", bestFor: "Field & travel projects", layoutType: "expense", accent: "#7c2d92" },
];